import { PropsWithChildren, createContext, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { StoreInterface } from '../store/types';
import { DeliveryMode, ORDER_METHODS } from '../services/exports/Constants';
import ReduxActions from '../store/ReduxActions';
import { actionCreators } from '../store/actions';
import moment from 'moment';
import useOrderMethodsStatus from '../hooks/availability/useOrderMethodsStatus';
import DeliveryQuotesManager from '../services/api/DeliveryQuotesManager';
import useDeepRef from '../hooks/utility/useDeepRef';
import useOrderSlots from '../hooks/availability/useOrderSlots';

export const SideEffectsContext = createContext<{
  loading: boolean;
  setLoading: (loading: boolean) => void;
}>({
  loading: false,
  setLoading: () => {},
});

const SideEffectsProvider = ({ children }: PropsWithChildren) => {
  const { company } = useSelector((state: StoreInterface) => state?.initialData);
  const { cached_order } = useSelector((state: StoreInterface) => state.order);
  const { profile } = useSelector((state: StoreInterface) => state.profile);
  const { enabledOrderMethods } = useOrderMethodsStatus();
  const [loading, setLoading] = useState<boolean>(false);
  const scheduledTimeRef = useDeepRef(cached_order?.scheduled_for);
  const loadingRef = useDeepRef(loading);
  const currentDate = moment();
  const disabled =
    company?.has_asap_orders ||
    !cached_order?.scheduled_for ||
    !(
      (!company?.has_pre_order && !moment(cached_order?.scheduled_for).isSame(moment(), 'day')) ||
      moment(cached_order?.scheduled_for).isBefore(moment(), 'day')
    );
  const { orderSlots, isLoading: isSlotsLoading } = useOrderSlots({
    orderMethod: cached_order?.method,
    date: currentDate,
    disabled,
  });

  /**
   * If cached order method is disabled - reset to the first enabled one
   */
  useEffect(() => {
    if (!enabledOrderMethods.includes(cached_order?.method)) {
      ReduxActions.dispatch(
        actionCreators.order.updateOrder({
          method: enabledOrderMethods[0] ?? ORDER_METHODS.PICKUP,
        }),
      );
    }
  }, [enabledOrderMethods, cached_order?.method]);

  const resetScheduledTime = useCallback(async () => {
    if (company?.has_asap_orders) {
      return ReduxActions.dispatch(actionCreators.order.resetScheduledTime());
    }

    if (isSlotsLoading || !orderSlots?.length) {
      return;
    }

    const newTime = orderSlots[0].start_time ?? null;

    ReduxActions.dispatch(
      actionCreators.order.resetScheduledTime({
        scheduled_for: newTime,
      }),
    );

    if (company?.delivery_mode !== DeliveryMode.Internal && !!cached_order?.delivery_quote?.id) {
      await DeliveryQuotesManager.refresh(cached_order.delivery_quote.id, { dropoff_time: newTime });
    }
  }, [
    company?.has_asap_orders,
    company?.id,
    company?.delivery_mode,
    cached_order?.method,
    cached_order?.delivery_quote?.id,
  ]);

  /**
   * Reset order schedule date if
   * - pre order is disabled and cached schedule date is set for other day than now
   * - cached order date is set to the past
   */
  useEffect(() => {
    if (!cached_order?.scheduled_for) {
      return;
    }

    if (
      (!company?.has_pre_order && !moment(cached_order?.scheduled_for).isSame(moment(), 'day')) ||
      moment(cached_order?.scheduled_for).isBefore(moment(), 'day')
    ) {
      resetScheduledTime();
    }
  }, [company?.has_pre_order, isSlotsLoading]);

  /**
   * Cache order delivery coordinates to local state from profile
   */
  useEffect(() => {
    cached_order?.method === ORDER_METHODS.DELIVERY &&
      profile?.delivery_lat &&
      profile?.delivery_lng &&
      !cached_order?.delivery_lat &&
      !cached_order?.delivery_lng &&
      ReduxActions.dispatch(
        actionCreators.order.updateOrder({
          delivery_lat: profile?.delivery_lat,
          delivery_lng: profile?.delivery_lng,
        }),
      );
  }, []);

  /**
   * Refresh expired delivery quote
   */
  useEffect(() => {
    const refreshDeliveryQuote = async () => {
      return (
        company?.delivery_mode !== DeliveryMode.Internal &&
        !!cached_order?.delivery_quote?.id &&
        !!cached_order?.delivery_quote?.expires_at &&
        moment(cached_order?.delivery_quote?.expires_at).isBefore(moment()) &&
        DeliveryQuotesManager.refresh(cached_order.delivery_quote.id, {
          dropoff_time: scheduledTimeRef.current ?? null,
        })
      );
    };

    refreshDeliveryQuote();
    const interval = setInterval(() => refreshDeliveryQuote(), 30000);

    return () => clearInterval(interval);
  }, [cached_order?.delivery_quote?.id, cached_order?.delivery_quote?.expires_at]);

  /**
   * Reset delivery_quote on location switch
   */
  useEffect(() => {
    if (!cached_order?.delivery_quote?.company_id || loadingRef.current) {
      return;
    }

    if (company?.id === cached_order?.delivery_quote?.company_id) {
      return;
    }

    ReduxActions.dispatch(
      actionCreators.order.updateOrder({
        delivery_quote: null,
      }),
    );
  }, [company?.id, cached_order?.delivery_quote?.company_id]);

  const context = useMemo(() => ({ loading, setLoading }), [loading]);

  return <SideEffectsContext.Provider value={context}>{children}</SideEffectsContext.Provider>;
};

export default SideEffectsProvider;
